/**
 * SEO utility functions for meta description optimization
 */

// SEO recommended lengths
const SEO_CONSTANTS = {
  MIN_DESCRIPTION_LENGTH: 120, // Minimum recommended length
  MAX_DESCRIPTION_LENGTH: 160, // Maximum recommended length for search results
  MAX_OG_DESCRIPTION_LENGTH: 200, // Maximum for Open Graph
  FALLBACK_DESCRIPTION:
    'SpotMe es un marketplace donde encuentras todo tipo de espacios en renta - desde estacionamientos, mini bodegas, bodegas y naves.',
  BRAND_SUFFIX: ' | SpotMe'
} as const

/**
 * Optimizes a meta description to meet SEO length requirements
 * @param description - The original description from backend
 * @param maxLength - Maximum length allowed (defaults to MAX_DESCRIPTION_LENGTH)
 * @param minLength - Minimum length required (defaults to MIN_DESCRIPTION_LENGTH)
 * @param fallback - Fallback description if original is too short
 * @returns Optimized description string
 */
export const optimizeMetaDescription = (
  description: string | null | undefined,
  maxLength: number = SEO_CONSTANTS.MAX_DESCRIPTION_LENGTH,
  minLength: number = SEO_CONSTANTS.MIN_DESCRIPTION_LENGTH,
  fallback: string = SEO_CONSTANTS.FALLBACK_DESCRIPTION
): string => {
  // Handle null/undefined descriptions
  if (!description || description.trim().length === 0) {
    return fallback
  }

  const cleanDescription = description.trim()

  // If description is already within optimal range, return as is
  if (
    cleanDescription.length >= minLength &&
    cleanDescription.length <= maxLength
  ) {
    return cleanDescription
  }

  // If description is too short, enhance it
  if (cleanDescription.length < minLength) {
    return enhanceShortDescription(cleanDescription, minLength, fallback)
  }

  // If description is too long, truncate it intelligently
  return truncateDescription(cleanDescription, maxLength)
}

/**
 * Enhances a short description to meet minimum length requirements
 * @param description - The short description
 * @param minLength - Minimum required length
 * @param fallback - Fallback description
 * @returns Enhanced description
 */
const enhanceShortDescription = (
  description: string,
  minLength: number,
  fallback: string
): string => {
  // If description is very short, use fallback
  if (description.length < 20) {
    return fallback
  }

  // Try to enhance with common phrases that add value
  const enhancements = [
    ' Encuentra espacios seguros y confiables.',
    ' Únete a nuestra comunidad para ofrecer o rentar espacios de manera segura.',
    ' Sin contratos largos, cancela cuando quieras.',
    ' Compara precios por zona y reserva en línea.',
    ' Proceso seguro desde la comodidad de tu casa.'
  ]

  for (const enhancement of enhancements) {
    const enhanced = description + enhancement
    if (
      enhanced.length >= minLength &&
      enhanced.length <= SEO_CONSTANTS.MAX_DESCRIPTION_LENGTH
    ) {
      return enhanced
    }
  }

  // If no enhancement fits, use fallback
  return fallback
}

/**
 * Intelligently truncates a description to fit within max length
 * @param description - The long description
 * @param maxLength - Maximum allowed length
 * @returns Truncated description
 */
const truncateDescription = (
  description: string,
  maxLength: number
): string => {
  if (description.length <= maxLength) {
    return description
  }

  // Try to truncate at sentence boundaries
  const sentences = description.split(/[.!?]+/)
  let truncated = ''

  for (const sentence of sentences) {
    const testTruncated = truncated + sentence + '.'
    if (testTruncated.length <= maxLength) {
      truncated = testTruncated
    } else {
      break
    }
  }

  // If we have a meaningful truncated version, use it
  if (truncated.length >= SEO_CONSTANTS.MIN_DESCRIPTION_LENGTH) {
    return truncated.trim()
  }

  // Otherwise, truncate at word boundary
  const words = description.split(' ')
  truncated = ''

  for (const word of words) {
    const testTruncated = truncated + (truncated ? ' ' : '') + word
    if (testTruncated.length <= maxLength - 3) {
      // Leave room for "..."
      truncated = testTruncated
    } else {
      break
    }
  }

  return truncated.trim() + (truncated.length < description.length ? '...' : '')
}

/**
 * Optimizes Open Graph description (allows longer descriptions)
 * @param description - The original description
 * @param fallback - Fallback description
 * @returns Optimized OG description
 */
export const optimizeOGDescription = (
  description: string | null | undefined,
  fallback: string = SEO_CONSTANTS.FALLBACK_DESCRIPTION
): string => {
  return optimizeMetaDescription(
    description,
    SEO_CONSTANTS.MAX_OG_DESCRIPTION_LENGTH,
    SEO_CONSTANTS.MIN_DESCRIPTION_LENGTH,
    fallback
  )
}

/**
 * Creates a complete SEO description object with both regular and OG descriptions
 * @param description - The original description from backend
 * @param fallback - Fallback description
 * @returns Object with optimized descriptions
 */
export const createSEODescriptions = (
  description: string | null | undefined,
  fallback: string = SEO_CONSTANTS.FALLBACK_DESCRIPTION
) => {
  return {
    description: optimizeMetaDescription(
      description,
      undefined,
      undefined,
      fallback
    ),
    descriptionOg: optimizeOGDescription(description, fallback)
  }
}

export { SEO_CONSTANTS }

const BASE_URL = 'https://www.spotme.mx'

export function getCanonicalUrl(path: string, locale?: string): string {
  const prefix = locale === 'en' ? '/en' : ''
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  return `${BASE_URL}${prefix}${normalizedPath}`
}

/**
 * Map a Next.js locale (`es` / `en`) to an Open Graph one.
 *
 * `og:locale` must be `language_TERRITORY` — a bare `es` is invalid and Facebook
 * drops it. Pages that pass their raw `locale` prop straight into
 * `openGraph.locale` were shipping `og:locale="es"` sitewide, which is *worse*
 * than the `DefaultSeo` fallback they were overriding.
 *
 * `es_MX`, not `es_ES`: SpotMe is a Mexico-only marketplace.
 */
export function toOgLocale(locale?: string): string {
  return locale === 'en' ? 'en_US' : 'es_MX'
}
